// English — legal & support texts. Receives the merchant details (C) from ../company.js.
export default (C) => ({
  ui: {
    header: "LEGAL & SUPPORT",
    back: "BACK",
    nav: "Legal documents",
    onThisPage: "ON THIS PAGE",
    lastUpdated: "LAST UPDATED",
    effective: "29 October 2026",
    companyDetails: "COMPANY DETAILS",
    cd: { company: "Company", nipt: "NIPT / Tax ID", office: "Registered office & service point", email: "E-mail", phone: "Phone" },
  },

  privacy: {
    kicker: "LEGAL · PRIVACY",
    title: "Privacy Policy",
    intro: "How Eagle Inc handles your personal data — on our websites, in our services, and when you pay us by card, Apple Pay, Google Pay, payment link or QR code.",
    sections: [
      {
        id: "who-we-are",
        title: "Who we are",
        body: [
          `This Privacy Policy explains how ${C.legalName} ("Eagle Inc", "we", "us") collects, uses and protects personal data when you visit any of our websites, contact us, receive an invoice or pay us by card, Apple Pay, Google Pay, payment link or QR code.`,
          "It applies to all websites of the Eagle Inc group, including eagle-inc.net, eagle-inc.al, eagle-inc.eu, eagle-eyes.al, eagle-commerce.al, digital-services.al, socialdeal.al, bookitnow.al and incontact.al, and to the services offered under the brands Eagle Eyes (Real Estate, IT Management, Digital Services) and Eagle Commerce.",
          {
            kv: [
              ["Controller", C.legalName],
              ["Trade names", C.tradeNames],
              ["NIPT / Tax ID", C.nuis],
              ["Registered address", C.address],
              ["Privacy contact", C.supportEmail],
              ["Phone", C.supportPhone],
            ],
          },
        ],
      },
      {
        id: "legal-framework",
        title: "Legal framework",
        body: [
          "We process personal data in accordance with Albanian Law no. 124/2024 \"On Personal Data Protection\". Where you are located in the European Union or the European Economic Area and the EU General Data Protection Regulation (GDPR, Regulation (EU) 2016/679) applies to our processing, we also comply with the GDPR.",
          "The supervisory authority in Albania is the Commissioner for the Right to Information and Personal Data Protection (Komisioneri për të Drejtën e Informimit dhe Mbrojtjen e të Dhënave Personale), https://idp.al.",
        ],
      },
      {
        id: "data-we-collect",
        title: "Personal data we collect",
        body: [
          "We only collect the data we need for the purposes described below:",
          {
            list: [
              "Identity and contact data — name, company name, e-mail address, phone number and postal or billing address, provided by you through our contact and support forms, by e-mail, phone or WhatsApp, or when we prepare a quote, contract or invoice.",
              "Contract and invoicing data — the services ordered, quotes, contracts, invoices, invoice numbers, amounts, currency, VAT details and correspondence about the engagement.",
              "Payment transaction data — payment status, date, amount, currency, payment method type (e.g. Visa, Mastercard, Apple Pay, Google Pay), an authorisation or transaction reference and, where the bank provides it, a masked card number (e.g. last four digits). We never receive or store full card numbers, expiry dates or CVV/CVC codes.",
              "Support data — the content of your support requests, refund requests and any information you choose to share with us when we help you.",
              "Technical data — IP address, browser type, device information and request logs that our hosting infrastructure records automatically for security and to deliver the website.",
            ],
          },
          "We do not knowingly collect special categories of personal data, and we ask you not to send us such data.",
        ],
      },
      {
        id: "card-payments",
        title: "Card payments, Apple Pay and Google Pay",
        body: [
          "Card payments are processed by our acquiring bank, Raiffeisen Bank Sh.A. (Albania), through its RaiAccept e-commerce service. When you pay through a RaiAccept payment link or QR code, you enter your card details — or authorise Apple Pay or Google Pay — on the bank's secure payment page, not on our website.",
          {
            list: [
              "Your card data is transmitted encrypted (TLS) directly to the bank and its PCI DSS-compliant payment infrastructure. Eagle Inc does not see, process or store your full card number or security code.",
              "Payments may be verified with 3-D Secure (e.g. Visa Secure, Mastercard Identity Check) by your card issuer.",
              "If you use Apple Pay or Google Pay, Apple or Google process your data under their own privacy policies; the bank receives a tokenised card number, not your real card number.",
              "Raiffeisen Bank, the card schemes (Visa, Mastercard) and your card issuer act as independent controllers for the data they process to execute, secure and settle the payment and to prevent fraud.",
              "We receive from the bank only the information needed to match the payment to your invoice and to handle refunds and disputes (see \"Personal data we collect\").",
            ],
          },
        ],
      },
      {
        id: "purposes",
        title: "Why we use your data and our legal basis",
        body: [
          {
            kv: [
              ["Answering your enquiries", "Taking steps at your request before entering into a contract; our legitimate interest in responding to messages."],
              ["Quotes, contracts and delivering services", "Performance of a contract with you."],
              ["Invoicing and collecting payment", "Performance of a contract; legal obligation (accounting and tax law)."],
              ["Refunds, chargebacks and payment disputes", "Performance of a contract; legal obligation; legitimate interest in defending legal claims."],
              ["Customer support", "Performance of a contract; legitimate interest in providing good service."],
              ["Fraud prevention and website security", "Legitimate interest in protecting our customers, our business and our systems; legal obligation where applicable."],
              ["Accounting, tax and regulatory records", "Legal obligation."],
              ["Remembering your language choice", "Legitimate interest in a functional website (stored only in your own browser)."],
            ],
          },
          "We do not sell personal data, we do not use it for automated decision-making that has legal or similarly significant effects on you, and we only send marketing messages if you have asked for them. You can unsubscribe at any time.",
        ],
      },
      {
        id: "recipients",
        title: "Who we share data with",
        body: [
          "We share personal data only where necessary, with:",
          {
            list: [
              "Raiffeisen Bank Sh.A. (Albania) and the card schemes and payment networks involved in processing your payment, and Apple or Google where you use their wallet.",
              "Microsoft (Microsoft Azure hosting and Microsoft 365 e-mail and productivity services) — acting as our processor.",
              "MongoDB Inc. (MongoDB Atlas database hosting for website form submissions) — acting as our processor.",
              "GitHub (source-code hosting and build pipeline; no customer data is stored there).",
              "Our accountants, auditors and legal advisers, under a duty of confidentiality.",
              "Public authorities, courts and law-enforcement bodies where we are legally required to do so.",
            ],
          },
          "Each processor is bound by a written agreement and may only use your data on our instructions.",
        ],
      },
      {
        id: "transfers",
        title: "International transfers",
        body: [
          "Some of our service providers (such as Microsoft and MongoDB) may process data in the European Union or in other countries outside Albania. Where data is transferred to a country that does not offer an adequate level of protection, we rely on appropriate safeguards provided for by Law no. 124/2024 and, where applicable, the GDPR — such as standard contractual clauses — and on the providers' own security commitments.",
        ],
      },
      {
        id: "retention",
        title: "How long we keep data",
        body: [
          {
            list: [
              "Enquiries and support requests that do not lead to a contract: up to 24 months after the last contact.",
              "Contracts, invoices, payment and accounting records: for the period required by Albanian accounting and tax legislation.",
              "Data needed for a dispute, refund or chargeback: until the matter is fully resolved and any related limitation period has expired.",
              "Technical log data: as a rule no longer than 90 days, unless needed to investigate a security incident.",
            ],
          },
          "After these periods, data is deleted or anonymised.",
        ],
      },
      {
        id: "cookies",
        title: "Cookies and local storage",
        body: [
          "Our websites do not use advertising or tracking cookies. We store a single item in your browser's local storage — your chosen display language — so the website remembers it. This item never leaves your device and you can delete it at any time through your browser settings.",
          "To display our typefaces, your browser loads fonts from Google Fonts and Fontshare, which receive your IP address as part of that technical request. RaiAccept's payment page, operated by the bank, may use its own strictly necessary cookies for security and fraud prevention.",
        ],
      },
      {
        id: "security",
        title: "How we protect your data",
        body: [
          "All our websites are served exclusively over HTTPS. Data is stored with reputable cloud providers, access is restricted to authorised persons with multi-factor authentication, and we apply the principle of least privilege. Card data never reaches our systems.",
          "If a personal data breach occurs that is likely to result in a risk to your rights, we will notify the Commissioner within 72 hours and inform you without undue delay where the risk is high, as required by law.",
        ],
      },
      {
        id: "your-rights",
        title: "Your rights",
        body: [
          "Subject to the conditions of the law, you have the right to:",
          {
            list: [
              "be informed about and obtain access to the personal data we hold about you;",
              "have inaccurate data corrected and incomplete data completed;",
              "have your data erased (\"right to be forgotten\");",
              "restrict the processing of your data;",
              "receive your data in a structured, machine-readable format (data portability);",
              "object to processing based on our legitimate interests, and to direct marketing at any time;",
              "withdraw any consent you have given, without affecting earlier processing.",
            ],
          },
          `To exercise your rights, e-mail ${C.supportEmail} or write to ${C.legalName}, ${C.address}. We may ask you to verify your identity and will respond within the time limits set by law (normally within one month).`,
          "You also have the right to lodge a complaint with the Commissioner for the Right to Information and Personal Data Protection (https://idp.al) or, in the EU/EEA, with the data protection authority of your country of residence.",
        ],
      },
      {
        id: "children",
        title: "Children",
        body: ["Our services are intended for businesses and adults. We do not knowingly collect personal data from children under 16."],
      },
      {
        id: "changes",
        title: "Changes to this policy",
        body: [
          "We may update this Privacy Policy from time to time. The current version is always available at /privacy, with the date of the last update shown at the top of the page. Material changes will be communicated to existing customers by e-mail.",
        ],
      },
      {
        id: "contact",
        title: "Contact",
        body: [
          { kv: [["Company", C.legalName], ["Address", C.address], ["E-mail", C.supportEmail], ["Phone", C.supportPhone], ["Support", "/support"]] },
        ],
      },
    ],
  },

  terms: {
    kicker: "LEGAL · TERMS OF USE",
    title: "Terms of Use",
    intro: "The terms that apply to our websites, our services and to payments made to Eagle Inc by card, Apple Pay, Google Pay, payment link or QR code — including our cancellation and refund policy.",
    sections: [
      {
        id: "merchant",
        title: "Merchant information",
        body: [
          "These Terms of Use govern the use of the websites of the Eagle Inc group and the purchase of and payment for our services. The merchant and seller for all payments is:",
          {
            kv: [
              ["Merchant", C.legalName],
              ["Trade names", C.tradeNames],
              ["NIPT / Tax ID", C.nuis],
              ["Registered address", C.address],
              ["Country", "Albania"],
              ["Customer support", `${C.supportEmail} · ${C.supportPhone}`],
              ["Support page", C.supportUrl],
            ],
          },
          "By using our websites, accepting a quote or paying an invoice, payment link or QR code issued by us, you agree to these Terms. If you do not agree, please do not use our websites or services.",
        ],
      },
      {
        id: "services",
        title: "Our services",
        body: [
          "Eagle Inc offers professional services under its brands, including IT management and IT consultancy (cloud, infrastructure, Microsoft 365/Azure, portfolio management), digital services and software products, and residential real estate services in Tirana.",
          "Our services are intangible: there is no physical shipping. The exact scope, deliverables, acceptance criteria, timeline and price of each engagement are set out in the written quote, proposal, contract or invoice we issue to you (the \"Order\"). If an Order conflicts with these Terms, the Order prevails.",
        ],
      },
      {
        id: "orders",
        title: "Quotes, orders and contract",
        body: [
          {
            list: [
              "A contract is formed when you accept our written quote or proposal (by e-mail, signature or by paying the related invoice or payment link).",
              "Quotes are valid for 30 days unless stated otherwise.",
              "One-off services and solutions are invoiced at the start of the engagement, unless the Order states otherwise.",
              "Ongoing services (such as managed IT and portfolio management) are provided under an annual contract with monthly invoicing, as set out in the Order.",
              "You are responsible for providing accurate information, timely access to systems and the cooperation we reasonably need to deliver the service.",
            ],
          },
        ],
      },
      {
        id: "prices",
        title: "Prices and currency",
        body: [
          "Prices are stated in the Order and on the invoice or payment link, in Albanian lek (ALL) or euro (EUR). The amount and currency shown on the RaiAccept payment page are the amount and currency you will be charged.",
          "Unless expressly stated otherwise, prices exclude VAT, which is added where applicable under Albanian tax law. If your card is denominated in another currency, your card issuer may apply its own exchange rate and fees; Eagle Inc has no control over these.",
        ],
      },
      {
        id: "payment",
        title: "Payment methods and payment security",
        body: [
          "We accept the following payment methods:",
          {
            list: [
              "Visa and Mastercard credit and debit cards;",
              "Apple Pay and Google Pay, where supported by your device and card;",
              "payment links and QR codes generated through RaiAccept and included on our invoices or sent to you by e-mail or message;",
              "bank transfer to the account stated on the invoice.",
            ],
          },
          "Card, Apple Pay and Google Pay payments are processed by our acquiring bank, Raiffeisen Bank Sh.A. (Albania), via its RaiAccept e-commerce service. Card details are entered only on the bank's secure, PCI DSS-compliant payment page over an encrypted (TLS) connection. Eagle Inc never sees or stores your full card number, expiry date or security code. Payments may require 3-D Secure authentication by your card issuer.",
          "A payment link or QR code is linked to a specific invoice and amount. Always check that the merchant name shown on the payment page is Eagle Inc before you pay. If a link has expired or does not work, contact support and we will issue a new one.",
          "Payment is due on the date stated on the invoice. We may suspend ongoing services if an invoice remains unpaid after a written reminder.",
          { note: `Eagle Inc will never ask you to send your full card number, expiry date or security code by e-mail, phone, chat or any other channel. If you receive such a request in our name, do not respond and report it to ${C.supportEmail}.` },
        ],
      },
      {
        id: "delivery",
        title: "Delivery of services",
        body: [
          "Services are delivered remotely and/or on site, as described in the Order. Delivery starts on the agreed start date or after receipt of payment where payment in advance is required. Deliverables (such as reports, configurations, documentation or access to software) are delivered electronically or on site. You will receive a confirmation by e-mail when a paid service has been delivered or a milestone has been completed.",
        ],
      },
      {
        id: "cancellation",
        title: "Cancellation and refund policy",
        body: [
          {
            list: [
              "Before work starts: you may cancel an Order in writing at any time before we begin performing the service and receive a full refund of any amount paid.",
              "After work has started: you may cancel in writing; you pay for the work performed and costs incurred up to the cancellation date, and any prepaid amount for work not yet performed is refunded.",
              "Annual contracts with monthly invoicing can be terminated as provided in the contract. Months already invoiced and delivered are not refundable unless the service was not provided.",
              "Duplicate or incorrect charges: if you were charged twice or charged an incorrect amount, we refund the excess in full.",
              "Services not provided as agreed: if we fail to deliver a service in accordance with the Order and cannot remedy this within a reasonable period, you are entitled to a full or proportional refund.",
            ],
          },
          `How to request a refund: e-mail ${C.supportEmail} or use the form at /support, stating your invoice number, payment date, amount and the reason. We confirm receipt within 1 business day and decide within 5 business days.`,
          "Approved refunds are made to the same card or payment method used for the original payment (including Apple Pay or Google Pay) — we do not refund card payments in cash or to a different card. We initiate the refund within 5 business days of approval; depending on your card issuer, it may take up to 10 business days before the amount appears on your statement.",
          "Where you act as a consumer, the mandatory consumer protection rules of Albanian law, including any statutory right of withdrawal for distance contracts, apply in addition to this policy and are not limited by it.",
        ],
      },
      {
        id: "disputes",
        title: "Payment disputes and chargebacks",
        body: [
          `If you do not recognise a charge from Eagle Inc or disagree with an amount, please contact us first at ${C.supportEmail} — most issues can be resolved quickly and refunded directly. This does not affect your right to dispute a transaction with your card issuer. If a chargeback is initiated, we will cooperate with the bank and provide the relevant Order, invoice and delivery documentation.`,
        ],
      },
      {
        id: "website-use",
        title: "Use of our websites",
        body: [
          "You may use our websites for lawful purposes only. You may not attempt to gain unauthorised access to our systems, interfere with the operation of the websites, submit false information or misuse our forms. We may change, suspend or discontinue any part of the websites at any time.",
          "All content on our websites — including texts, logos, the Eagle medallion, designs, images and software — is owned by or licensed to Eagle Inc and protected by intellectual property law. You may not copy or reuse it without our prior written permission.",
          "Information on our websites is general and may change. It does not constitute an offer unless confirmed in an Order. Links to third-party websites are provided for convenience; we are not responsible for their content.",
        ],
      },
      {
        id: "liability",
        title: "Liability",
        body: [
          "We perform our services with due care and in accordance with good professional practice. To the extent permitted by law, our total liability arising from an Order is limited to the amount paid by you under that Order in the twelve months preceding the event giving rise to the claim, and we are not liable for indirect or consequential loss, including loss of profit or data.",
          "Nothing in these Terms excludes or limits liability that cannot be excluded or limited under applicable law, including liability for intent or gross negligence, or your mandatory rights as a consumer.",
        ],
      },
      {
        id: "privacy",
        title: "Privacy",
        body: ["We process personal data in accordance with our Privacy Policy, available at /privacy."],
      },
      {
        id: "force-majeure",
        title: "Force majeure",
        body: ["Neither party is liable for failure or delay in performance caused by events beyond its reasonable control, such as natural disasters, power or network outages of third-party providers, war, strikes or government action. Obligations are suspended for the duration of such an event."],
      },
      {
        id: "law",
        title: "Governing law and disputes",
        body: [
          "These Terms and every Order are governed by the laws of the Republic of Albania. We will always try to resolve complaints amicably first — please contact support. Disputes that cannot be resolved amicably fall under the jurisdiction of the competent court in Tirana, Albania, without prejudice to any mandatory right of a consumer to bring proceedings in the courts of their place of residence.",
        ],
      },
      {
        id: "changes",
        title: "Changes to these Terms",
        body: [
          "We may amend these Terms from time to time. The version published at /termsofuse on the date of your Order applies to that Order. The date of the last update is shown at the top of this page.",
        ],
      },
    ],
  },

  support: {
    kicker: "CUSTOMER SUPPORT",
    title: "Support",
    intro: "Help with payments, invoices, refunds and our services. Our support team answers every request personally.",
    tocContact: "Contact customer support",
    tocRequest: "Send a support request",
    email: "E-mail",
    phone: "Phone",
    hoursLabel: "Support hours",
    hours: "Monday – Friday, 09:00 – 17:00 (CET/CEST, Tirana time). First response within 1 business day.",
    security: "For your security, never send us your full card number, expiry date or security code (CVV/CVC). To identify a payment, the invoice number, date, amount and the last four digits of your card are enough.",
    requestTitle: "Send a support request",
    faqTitle: "FREQUENTLY ASKED QUESTIONS",
    topics: [
      "Payment problem (declined, failed, expired link or QR code)",
      "Refund request",
      "Double or incorrect charge",
      "Question about an invoice",
      "Question about a service or order",
      "Privacy / personal data request",
      "Other",
    ],
    form: {
      name: "Full name",
      email: "E-mail address",
      reference: "Invoice number or reference (optional)",
      message: "How can we help? Never include your full card number or security code.",
      consent: "By sending this form you agree that we process your details to handle your request, as described in /privacy.",
      send: "SEND REQUEST",
      sending: "SENDING…",
      sentTitle: "Thank you — your request has been received.",
      sentBody: (email) => `We will reply within 1 business day to ${email}.`,
      error: `We could not send your message. Please e-mail ${C.supportEmail} or call ${C.supportPhone}.`,
    },
    faq: [
      {
        id: "pay-how",
        title: "How do I pay an invoice?",
        body: [
          "Every invoice contains a secure RaiAccept payment link and QR code from Raiffeisen Bank Albania. Click the link or scan the QR code with your phone camera, check the amount and that the merchant is Eagle Inc, and pay with Visa, Mastercard, Apple Pay or Google Pay. You can also pay by bank transfer to the account stated on the invoice.",
        ],
      },
      {
        id: "pay-failed",
        title: "My payment was declined or failed",
        body: [
          {
            list: [
              "Check that the card details, expiry date and billing details are correct and that online/international payments are enabled on your card.",
              "Complete the 3-D Secure verification in your banking app or by SMS code — the payment fails if this step times out.",
              "Make sure the card has sufficient available balance or limit.",
              "Try Apple Pay or Google Pay, another card, or contact your card issuer — declines are decided by the issuing bank, which can tell you the reason.",
            ],
          },
          "A declined payment is not charged. If an amount is shown as reserved (pending) on your account after a failed attempt, it is released automatically by your bank, normally within a few business days.",
        ],
      },
      {
        id: "pay-link",
        title: "The payment link or QR code has expired or does not open",
        body: ["Payment links and QR codes are valid for a limited time and for one specific amount. Contact us with your invoice number and we will send you a new link within 1 business day."],
      },
      {
        id: "double",
        title: "I was charged twice or the wrong amount",
        body: ["Contact us with your invoice number, the date and amount of each charge and the last four digits of your card. Duplicate or incorrect charges are refunded in full to the original card."],
      },
      {
        id: "refund",
        title: "How do I request a refund?",
        body: [
          `Use the form above or e-mail ${C.supportEmail} with your invoice number, payment date, amount and the reason for your request. We confirm receipt within 1 business day and decide within 5 business days. Approved refunds are made to the same card or wallet used for the payment and are initiated within 5 business days; your card issuer may need up to 10 business days to show the amount on your statement. The full policy is in our Terms of Use: /termsofuse.`,
        ],
      },
      {
        id: "receipt",
        title: "Where is my receipt or invoice?",
        body: ["After a successful payment you receive a payment confirmation from the payment page. We send the invoice and, on request, a paid confirmation to the e-mail address we have on file. Ask support if you need a copy."],
      },
      {
        id: "complaint",
        title: "Complaints and escalation",
        body: [
          `If you are not satisfied with the handling of your request, reply to our message asking for escalation — your complaint will be reviewed by the management of ${C.legalName} and answered within 10 business days. You always keep the right to contact your card issuer or the competent authorities.`,
        ],
      },
    ],
  },
});
